import axios from 'axios';
import querystring from 'querystring';

/**
 * ดึงค่า Company Config จาก API
 * @param {string} company - รหัสบริษัท
 * @param {string} configKey - Config Key ที่ต้องการดึง
 * @param {Object} options - ตัวเลือกเพิ่มเติม
 * @param {Object} options.cancelToken - Axios cancel token (ถ้ามี)
 * @param {string} options.accessToken - Access token (ถ้ายังไม่มีใน localStorage เช่น ตอน login / หลัง clear session)
 * @returns {Promise<string|null>} - คืนค่า ConfigValue หรือ null ถ้าไม่พบ
 */
export const getCompanyConfig = async (company, configKey, defaultValue = null, options = {}) => {
    try {
        const params = {
            company: company || "",
            configKey: configKey
        };

        const queryString = querystring.stringify(params);
        const url = `${process.env.REACT_APP_API_URL}companyConfig?${queryString}`;

        const config = {
            headers: {
                "Authorization": `Bearer ${options.accessToken || localStorage.getItem('x-access-token')}`
            }
        };

        // เพิ่ม cancelToken ถ้ามี
        if (options.cancelToken) {
            config.cancelToken = options.cancelToken;
        }

        const result = await axios.get(url, config);
        const resultData = result.data;

        if (resultData && resultData.length > 0) {
            return resultData[0].ConfigValue;
        }

        return defaultValue;

    } catch (error) {
        if (axios.isCancel(error)) {
            console.log('Request canceled:', error.message);
        } else {
            console.error('Error fetching company config:', error);
        }
        return defaultValue;
    }
};

/**
 * ดึงค่า Company Config หลายตัวพร้อมกัน
 * @param {string} company - รหัสบริษัท
 * @param {string[]} configKeys - Array ของ Config Keys ที่ต้องการดึง
 * @param {Object} options - ตัวเลือกเพิ่มเติม
 * @returns {Promise<Object>} - คืนค่าเป็น Object {configKey: configValue}
 */
export const getMultipleCompanyConfigs = async (company, configKeys, options = {}) => {
    try {
        const promises = configKeys.map(key => 
            getCompanyConfig(company, key, options)
        );

        const results = await Promise.all(promises);

        // แปลงเป็น Object
        const configObject = {};
        configKeys.forEach((key, index) => {
            configObject[key] = results[index];
        });

        return configObject;

    } catch (error) {
        console.error('Error fetching multiple company configs:', error);
        return {};
    }
};

/**
 * ดึงค่า Company Config แบบมี default value
 * @param {string} company - รหัสบริษัท
 * @param {string} configKey - Config Key ที่ต้องการดึง
 * @param {string} defaultValue - ค่า default ถ้าไม่พบ config
 * @param {Object} options - ตัวเลือกเพิ่มเติม
 * @returns {Promise<string>} - คืนค่า ConfigValue หรือ defaultValue
 */
export const getCompanyConfigWithDefault = async (company, configKey, defaultValue = "", options = {}) => {
    const value = await getCompanyConfig(company, configKey, options);
    return value !== null ? value : defaultValue;
};
